import { Location } from '@angular/common';
import { Injectable, Input } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { NavController, Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom, iif, of, Subject, timer } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  skipWhile,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { AppSettings } from '../../../app.settings';
import {
  Counters,
  getConfig,
  getGetmTienda,
  getProducts,
  getProfile,
  getPromosEcommerce,
  resizeview,
  sendCart,
  sendConnection,
  sendEcommerceConfig,
  sendObtienePedidos,
  sendStatusLifesModal,
  setCounter,
  updatedUserData,
  updateModal,
  validatedJwtBOUser,
  validatedJwtWaUser,
} from '../../../store/actions';
import { AppState } from '../../../store/app-reducers';
import {
  CartProductsState,
  CountersSaveState,
  IGetStatusModal,
  ResizeViewState,
  UpdatedState,
} from '../../../store/reducers';
import { selectOrdersState } from '../../../store/selectors/ecommerce-orders.selectors';
import { SharedAnalyticsService } from '../../analytics/shared-analytics/shared-analytics.service';
import { DisabledUserComponent } from '../../components/disabled-user/disabled-user.component';
import { EAnalyticsEvents, EAnalyticsModals, EAnalyticsPageViews, ESectionProperty } from '../../enums/analytics.enum';
import { GTMEventsCrash } from '../../enums/Crashlytics';
import { PATHS_IGNORE_BACK } from '../../enums/paths.enum';
import { IGNORE } from '../../enums/versiones';
import { HandleErrorService } from '../../interceptors/handle-error.service';
import { CoolerWelcomePage } from '../../modals/cooler-welcome/cooler-welcome.page';
import {
  DailyRewardPage,
  LegalChangePage,
  NewSectionGamePage,
  RewardModalPage,
  TimeoutEditPage,
} from '../../modals/index.modals';
import { PromotionPage } from '../../modals/promotion/promotion.page';
import { IInteractionModalEvent, PromoModalInteractionParams, PromoModalViewParams } from '../../models/Analytics';
import { ApplicationUser } from '../../models/ApplicationUser';
import {
  DATA_STORAGE_KEYS,
  ECOMMERCE_ENDPOINTS,
  ECOMMERCE_V2_URLS,
  EventInteraction,
  GTMInteractions,
  MenuTabs,
  minWidthDesktop,
  NativeStorageNamesV2,
  NO_GUARDS,
  SECTIONS,
  SHOP_URLS,
  UPDATE_CART_URL_ONPAUSE,
  URLS_GAMES,
  URLS_IGNORE_DESKTOP,
  URLS_SENDCART,
} from '../../models/Constants';
import { AnalyticsService } from '../analytics.service';
import { AuthService } from '../auth-service';
import { CrashlyticsService } from '../crashlytics/crashlytics.service';
import { EncryptionService } from '../encryption/encryption.service';
import { FcmTokenService } from '../fcm/fcm-token.service';
import { ModalsService } from '../modals/modals.service';
import { OperationsService } from '../operations/operations.service';
import { SharedServiceService } from '../shared-service.service';
import { StorageData } from '../utilities/storage-data.class';
import { UtilitiesService } from '../utilities/utilities.service';

@Injectable({
  providedIn: 'root'
})
export class DispatchInitService {

  @Input() stopInitAfterView = false;

  private FechaIngreso = new Date().toISOString().slice(0, 19).replace(/T/, ' ');
  private loginWAEventSended = false;
  private eventPause$: Subject<CustomEvent> = new Subject();
  private eventLoadRoute$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private legalchange: boolean = false;

  constructor(
    private readonly fcmTokenS: FcmTokenService
    , private handleErrorS: HandleErrorService
    , private crashlytics: CrashlyticsService
    , private operationsS: OperationsService
    , private encryptionS: EncryptionService
    , private sharedS: SharedServiceService
    , private analyticsS: AnalyticsService
    , private sharedAnalyticsS: SharedAnalyticsService
    , private translate: TranslateService
    , private utilS: UtilitiesService
    , private modalsS: ModalsService
    , private authS: AuthService
    , private readonly location: Location
    , private actRoute: ActivatedRoute
    , private store: Store<AppState>
    , private platform: Platform
    , private nav: NavController
    , private router: Router
  ) {
    this.callbackResizeView();
  }

  public initAfterView = async () => {
    const isIgnore = await IGNORE();
    const state$ = this.sharedS.conexionState.pipe(debounceTime(300), filter(state => state));
    this.store.select('StatusLifesModal')
      .pipe(
        switchMap(message => state$.pipe(map(() => message))),
        map(value => value.GetStatus),
        switchMap(x => of(x).pipe(skipWhile(y => !y.success && !y.failed))),
        map(value => value.status),
        distinctUntilChanged(),
        switchMap(status => iif(() => isIgnore, of(0), of(status))),
      )
      .subscribe(this.callbackStatusLifesModal);

    this.store.select('Modal')
      .pipe(
        switchMap(message => state$.pipe(map(() => message))),
        map(value => value.GetStatus),
        filter(x => x.success || x.failed),
        distinctUntilChanged((prev, curr) => prev.status === curr.status),
      )
      .subscribe(this.callbackPopUp);

    this.eventPause$
      .pipe(
        switchMap(() => this.store.select('userData')
          .pipe(
            skipWhile(user => user.ID_Bodega === 0),
            filter(user => user.ID_Bodega > 0),
            take(1),
          )
        )
      )
      .subscribe(this.callbackSendDeviceInfo);

    this.operationsS.combinerCartOrder$()
      .pipe(
        switchMap(cart => this.store.select(selectOrdersState).pipe(filter(x => x.success || x.failed), map(() => cart))),
        switchMap(cart => of(this.location.path().split('?token=')[0])
          .pipe(skipWhile(crt_url => !URLS_SENDCART.some(url => crt_url === url)), map(() => cart))
        ),
        debounceTime(500),
      )
      .subscribe(this.callbackCartSend);

    this.store.select('CountersSave')
      .pipe(
        skipWhile(x => !x.success && !x.failed),
      )
      .subscribe(this.callbackCounterSave);

    this.sharedS.bodegaNud$
      .pipe(
        distinctUntilChanged((prev, curr) => prev.ID_Bodega === curr.ID_Bodega && prev.NUD === curr.NUD),
        filter(data => data.ID_Bodega > 0 && data.NUD > 0),
      )
      .subscribe(() => this.store.dispatch(getGetmTienda()));

  }


  private callbackPopUp = async (getStatus: IGetStatusModal) => {
    if (this.legalchange) return;
    const { idCatpopup, descripcion, type, status } = getStatus;
    let url = '';
    let popup: PromoModalInteractionParams;
    let component: any;
    switch (idCatpopup) { // ? Switch para identificar que popup se debe mostrar
      case 1:
        url = '/Promociones';
        popup = {
          ...getStatus,
          title: this.translate.instant('Labels.title-promotion-modal'),
          callToActionText: this.translate.instant('Buttons.go-to-promotion'),
          callToAction: 'btn_go-to-promotion'
        };
        component = PromotionPage;
        break;
      case 2:
        url = '/main/Tienda';
        popup = {
          ...getStatus,
          title: this.translate.instant('Labels.title-sugar-tax-modal'),
          callToActionText: this.translate.instant('Buttons.go-to-order'),
          callToAction: 'btn_go-to-store'
        };
        component = PromotionPage;
        break;
      case 3:
        url = '/Enfriadores';
        popup = {
          ...getStatus,
          title: this.translate.instant('Titles.cooler-request-modal'),
          callToActionText: this.translate.instant('Buttons.go-to-coolers'),
          callToAction: 'btn_go-to-coolers'
        };
        component = CoolerWelcomePage;
        break;
      case 4:
        popup = {
          ...getStatus,
          title: this.translate.instant('Modals.reward-promo.title'),
          callToActionText: this.translate.instant('Modals.reward-promo.closeButton'),
          callToAction: ''
        };
        component = RewardModalPage;
        break;
    }
    if (status != 1) return;

    this.sendEventViewPromoModal({ idCatpopup, descripcion, type, title: popup.title });
    let actionType: number = 0;
    switch (idCatpopup) { // ? Switch para identificar que modal se debe mostrar, agregar case si es personalizado o pasa al default
      case 4:
        actionType = await this.modalsS.presentModalReward(RewardModalPage);
        break;
      default:
        actionType = await this.modalsS.openModalPromotion(component, idCatpopup);
        break;
    }

    switch (actionType) { // ? Switch para identificar que accion se debe realizar al cerrar el modal
      case 1:
        this.store.dispatch(updateModal({ MostrarMas: false }));
        this.sendEventInteractionPromoModal(popup);
        this.nav.navigateForward([url], { animated: true, animationDirection: 'forward' });
        break;
      case 2:
        popup.callToAction = 'link_view-later'
        popup.callToActionText = this.translate.instant('Buttons.view-later')
        this.store.dispatch(updateModal({ MostrarMas: true }))
        this.sendEventInteractionPromoModal(popup);
        break;
      default: // * No hace nada, solo dissmis, se tiene que mandar el 0
        this.store.dispatch(updateModal({ MostrarMas: false }));
        this.sendEventInteractionPromoModal(popup);
        break;
    }
  }

  /**
   * Callback para validar si se aparece la modal de la aviso incial o recompensa de vidas diaria
   */
  private callbackStatusLifesModal = async (status: number) => {
    let isGame = false;
    switch (status) {
      case 0:
        break;
      case 1:
        const activeModalNewSectionGame: boolean = this.translate.instant('Miscellaneous.modal-new-section-game');
        if (activeModalNewSectionGame) {
          this.sharedAnalyticsS.trackPageView(EAnalyticsPageViews.newSectionGamesView, SECTIONS.TIENDA);

          isGame = await this.modalsS.modalNewSectionGames(NewSectionGamePage);
          if (isGame) {
            this.store.dispatch(sendStatusLifesModal({ status }));
            this.nav.navigateForward(['/Juegos'], { animated: true, animationDirection: 'forward' });
          }

          const eventClickGame = isGame ? GTMInteractions.playNewSectionEvent : GTMInteractions.playLaterNewSectionEvent;
          this.analyticsS.sendEventAnalytics(eventClickGame, EventInteraction);
        }
        break;
      case 2:
        const activeModal: boolean = this.translate.instant('Miscellaneous.modal-daily-lives');
        if (activeModal) {
          this.sharedAnalyticsS.trackPageView(EAnalyticsPageViews.dailyLifesGamesView, SECTIONS.TIENDA);

          isGame = await this.modalsS.modalGames(DailyRewardPage);
          if (isGame) {
            this.store.dispatch(sendStatusLifesModal({ status }));
            this.nav.navigateForward(['/Juegos'], { animated: true, animationDirection: 'forward' });
          }

          const eventClickLifes = isGame ? GTMInteractions.playDailyLifesEvent : GTMInteractions.playLaterDailyLifesEvent;
          this.analyticsS.sendEventAnalytics(eventClickLifes, EventInteraction);
        }
        break;
    }
  }

  /**
   * Reincia la fecha con hora el ingreso a la app
   */
  public newFechaIngreso = () => this.FechaIngreso = new Date().toISOString().slice(0, 19).replace(/T/, ' ');

  /**
   * Crea suscripciones a los eventos nativos de las aplicaciones
   * moviles (pause y resume), ejecuta los procesos necesarios
   * en estos eventos
   */
  public appPriorityStates = () => {
    // Evento de 'pause'
    App.addListener('pause', () => {
      this.eventPause$.next(new CustomEvent('pause', { bubbles: true }));
    });

    // Evento de 'resume'
    App.addListener('resume', () => this.handleAppResume());

    // Evento de 'resize'
    window.addEventListener('resize', this.callbackResizeView);

    // Evento de 'back button'
    this.platform.backButton.subscribeWithPriority(10, async () => {
      if (PATHS_IGNORE_BACK[window.location.pathname]) return;

      const isModals = await this.modalsS.modalActives();
      if (isModals) {
        await this.modalsS.modalDismissActive();
        return;
      }
      const loadingTop = await this.sharedS.loadingGetop() ? true : false;
      if (loadingTop) {
        this.sharedS.loadingDismissActive();
        return;
      }

      if (window.history.length > 1) {
        window.history.back();
        return;
      }
    });

    // Evento de cambios de URL's
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        // Aquí puedes ejecutar tu lógica personalizada: ('URL actual:' event.urlAfterRedirects)
        this.changesColorBarDinamic(event);
      });
  }

  /**
   * Verifica si la URL actual y verifica si es de Ecommerce V2 o Games y cambia el color de la barra de estado dinamicamente
   * @param event Evento de navegación
   */
  private changesColorBarDinamic = async (event: NavigationEnd) => {
    if (!Capacitor.isNativePlatform()) return;
    const urlCrt = event.urlAfterRedirects;
    const docElement = getComputedStyle(document.documentElement);
    let defaultColor = docElement.getPropertyValue('--ion-color-darkblue').trim();

    // TODO: Solo el nuevo ecommerce tiene este color, cuando se migre todo el app, este deberia ser el default
    const isUrlsNewEco = ECOMMERCE_V2_URLS.some(eco => urlCrt.includes(eco));
    if (isUrlsNewEco) {
      defaultColor = docElement.getPropertyValue('--ion-color-getm-primary-default').trim();
    }
    const isUrlsGames = URLS_GAMES.some(game => urlCrt.includes(game));
    if (isUrlsGames) {
      defaultColor = urlCrt.includes('Leaderboard') ? '#B956FF' : '#207ede';
    }

    await StatusBar.setBackgroundColor({ color: defaultColor });
    if (Capacitor.getPlatform() === 'android') await EdgeToEdge.setBackgroundColor({ color: defaultColor });
  }

  /**
   * Maneja el evento de resume de la app.
   * Verifica permisos de notificación y renueva el token FCM si está próximo a expirar.
   */
  public handleAppResume = async (): Promise<void> => {
    // Verificar permisos de notificación (incluye renovación mensual del token si es necesario)
    await this.authS.checkAndStoreNotificationPermission();

    // Verificar si el token está próximo a expirar por inactividad (270 días según Google)
    const isNearExpiration = await this.fcmTokenS.isNearExpiration();
    if (isNearExpiration) {
      // Forzar renovación del token para evitar invalidación
      await this.fcmTokenS.refreshIfNeeded(true);
    }
  }

  /**
   * Inicializa el listener de cambios de token FCM.
   * Utiliza FcmTokenService directamente para la gestión de tokens.
   */
  public initFCMTokenListener = async (): Promise<void> => {
    await this.fcmTokenS.initTokenListener();

    // Suscribirse a cambios de token para sincronizar con backend
    this.fcmTokenS.tokenChanged$.subscribe(async (newToken) => {
      if (newToken) {
        // forceSync: true porque el listener ya detectó un cambio de token
        await this.fcmTokenS.syncPushPermissionsForCurrentUser(undefined, true);
      }
    });
  }

  public visibilityMenu$ = () => this.store.select('resizeView')
    .pipe(
      map(this.isWidthMobile)
    )

  /**
   * Envia la info del device y fechas de ingreso y egreso del usuario
   */
  public callbackSendDeviceInfo = () => {
    this.store.dispatch(sendConnection(this.getDeviceInfo()));
    if (UPDATE_CART_URL_ONPAUSE.some(url => this.location.path() === url)) this.store.dispatch(sendCart());
  }

  /**
   * Arma el objeto de la informacion del dispositivo y app movil del usuario
   * para guardar en la BD
   */
  private getDeviceInfo = () => ({
    DeviceID: this.sharedS.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).uniqueDeviceID
    , DeviceInfo: this.sharedS.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).deviceInfo
    , Version: `v${environment.versionNumber} (${environment.versionCode})`
    , Fecha_Ingreso: this.FechaIngreso
    , Fecha_Egreso: new Date().toISOString().slice(0, 19).replace(/T/, ' ')
  });

  /**
   * Actualiza el tamaño width-height del navegador/dispositivo
   */
  private callbackResizeView = () => this.store.dispatch(resizeview({ height: window.innerHeight, width: window.innerWidth }));

  private isWidthMobile = (resize: ResizeViewState) => {
    const currentPath = this.router.routerState.snapshot.url || this.location.path();
    return resize.width >= minWidthDesktop && !URLS_IGNORE_DESKTOP.some(url => url === currentPath);
  };

  public getConfigurations = async () => {
    this.sharedS.LlaveConfiguracion = this.translate.instant('Miscellaneous.dispatch_schedule');
    this.store.dispatch(getConfig({ LlaveConfiguracion: this.sharedS.LlaveConfiguracion }));
    this.store.dispatch(Counters());
  }

  /**
   * Detecta si existe un token como parametro en la URL y valida si es un token
   * vigente para iniciar sesion del usuario
   */
  public detectUserByToken = () => {
    const ValidJwtWa$ = this.store.select('ValidJwtWa')
      .pipe(
        map(value => value.Activo),
        distinctUntilChanged(),
      );

    this.actRoute.queryParams
      .pipe(
        filter(params => Object.keys(params).length > 0),
        map(qp => {
          if (this.location.path().includes('token')) {
            const token = this.location.path().split('?token=')[1];
            return { token };
          }
          return qp;
        }),
        map(params => {
          this.eventLoadRoute$.next(true);
          return params;
        }),
        filter(params => params.hasOwnProperty('token')),
        distinctUntilKeyChanged('token'),
        tap(({ token }) => {
          this.handleErrorS.isSaveToken = false;
          const user = this.utilS.getDecodeTokenJwt(token);
          if (user) {
            this.sharedS.bodegaNud$.next({
              ID_Bodega: user.ID_Bodega
              , NUD: user.NUD
              , bodega: user.ID_Bodega
              , nud: user.NUD
              , redsocial: 2
              , ID_Chat: user.ID_HistorialConversacionChat
            });
          }
          this.store.dispatch(validatedJwtWaUser({ Clave_Acceso: token, ID_Chat: user.ID_HistorialConversacionChat }));
          this.handleErrorS.JWT_L$ = token.split('.')[1];
        }),
        switchMap(params => ValidJwtWa$.pipe(map(isValid => ({ isValid, token: params.token })))),
        map(({ isValid, token }) => {
          const isActive = this.utilS.isActiveToken(token);
          return {
            token,
            isValid,
            isActive
          };
        }),
        tap(() => this.store.dispatch(getProfile())),
      )
      .subscribe(this.getUserDataByToken);
  }

  private getUserDataByToken = async (params: { token: string, isActive: boolean, isValid: boolean }) => {
    if (!SHOP_URLS.some(url => url === this.location.path().split('?token=')[0])) {
      this.nav.navigateBack(['/Tienda'], { animated: true, animationDirection: 'back', queryParams: { token: params.token } });
    }
    await SplashScreen.hide();
    this.sharedS.isUrlWithTokenWA$.next(true);
    this.stopInitAfterView = true;
    if (params.token && params.isActive && params.isValid) { // ? Token valido en url
      // * Hace peticiones del usuario del token
      StorageData.removeDataStorageMultiple([
        ECOMMERCE_ENDPOINTS.get_products.url,
        NativeStorageNamesV2.ObtienePromocionesEcommerce,
      ]);
      this.store.dispatch(sendObtienePedidos());
      this.store.dispatch(sendEcommerceConfig());
      this.store.dispatch(getProducts({ endpoint: ECOMMERCE_ENDPOINTS.get_products }));
      this.store.dispatch(getPromosEcommerce());
      this.sharedS.urlWithTokenWA$.next(params.token);

      // ? Manejo de edicion
      const respPedidos = await firstValueFrom(this.store.select(selectOrdersState)
        .pipe(
          skipWhile(x => !x.success && !x.failed),
          map(value => value.respPedidos),
          first(),
          map(resp => ({ ...resp, pedido_actual: resp.pedido_actual?.filter((p => p.status === 1)) || [] })),
        ));
      if (respPedidos.pedido_actual.length > 0 && respPedidos.pedido_actual.some(p => p.status === 1) && respPedidos.pedido_actual[0].detalle) {
        // ? Programa tiempo de edición
        const user = this.utilS.getDecodeTokenJwt(params.token);
        timer(moment(user.exp).toDate())
          .pipe(first())
          .subscribe(async () => {
            const isRe = await this.modalsS.modalTimeOutEdit(TimeoutEditPage);
            this.sharedS.renewToken$.next(isRe);
          });
      }

      // ? Envio de evento de login por whatsapp
      const userProfile = await firstValueFrom(this.store.select('profile').pipe(first(state => state.success || state.failed)));
      if (!this.loginWAEventSended && userProfile.success) {
        this.loginWAEventSended = true;
        const loginEvent = {
          ...EAnalyticsEvents.loginEvent,
          number: this.encryptionS.encrypt(userProfile.data.TelefonoWhatsapp),
          user_id: userProfile.data.ID_Tendero,
          usr_id: `${userProfile.data.ID_Bodega}|${userProfile.data.NUD}`,
        };
        await this.analyticsS.sendEventGTM(loginEvent, { addEnvironmentProps: true });
      }
    } else { // ? Token valido en url, mostrar de pantalla caduco
      this.nav.navigateRoot(['/TokenExpirado'], { animated: true, animationDirection: 'forward', queryParams: { token: params.token } });
    }
    this.eventLoadRoute$.next(false);
  }

  /**
   * Detecta si existe un token de tipo backoffice como parametro en la URL y valida si es un token
   * vigente para iniciar sesion del usuario espejo
   */
  public detectUserByBOToken = () => {
    const ValidJwtBO$ = this.store.select('ValidJwtBO')
      .pipe(
        map(value => value.Activo),
        distinctUntilChanged(),
      );

    this.actRoute.queryParams
      .pipe(
        filter(params => Object.keys(params).length > 0),
        map(qp => {
          if (this.location.path().includes('backoffice')) {
            const backoffice = this.location.path().split('?backoffice=')[1];
            return { backoffice };
          }
          return qp;
        }),
        filter(params => params.hasOwnProperty('backoffice')),
        distinctUntilKeyChanged('backoffice'),
        tap(() => this.eventLoadRoute$.next(true)),
        tap(({ backoffice }) => {
          this.handleErrorS.isSaveToken = false;
          const user = this.utilS.getDecodeTokenJwt(backoffice);
          if (user) {
            this.sharedS.bodegaNud$.next({
              ID_Bodega: user.ID_Bodega
              , NUD: user.NUD
              , bodega: user.ID_Bodega
              , nud: user.NUD
            });
          }
          this.handleErrorS.JWT_L$ = backoffice;
          this.store.dispatch(validatedJwtBOUser({ token: backoffice }));
        }),
        switchMap(params => ValidJwtBO$.pipe(map(isValid => ({ isValid, token: params.backoffice })))),
        map(({ isValid, token }) => {
          const isActive = this.utilS.isActiveToken(token);
          return {
            token,
            isValid,
            isActive
          };
        }),
        tap(() => this.store.dispatch(getProfile())),
      )
      .subscribe(this.getUserDataByBOToken);
  }

  private getUserDataByBOToken = async (params: { token: string, isActive: boolean, isValid: boolean }) => {
    StorageData.removeDataStorageMultiple(DATA_STORAGE_KEYS);
    await SplashScreen.hide();
    if (params.token && params.isActive && params.isValid) { // ? Token valido en url
      const user = this.utilS.getDecodeTokenJwt(params.token);
      // * Hace peticiones del usuario del token
      StorageData.setDataStorage(NativeStorageNamesV2.BodegaNud, [user.ID_Bodega, user.NUD, user.ID_Programa, user.ID_TenderoRegistro, user.ID_Tendero, false]);

      // ? Programa tiempo de edición
      timer(moment(user.exp).toDate())
        .pipe(first())
        .subscribe(async () => {
          this.stopInitAfterView = true;
          this.nav.navigateRoot(['/TokenExpirado'], { animated: true, animationDirection: 'forward', queryParams: { backoffice: this.handleErrorS.JWT_L$ } });
        });
    } else { // ? Token valido en url, mostrar de pantalla caduco
      this.stopInitAfterView = true;
      this.nav.navigateRoot(['/TokenExpirado'], { animated: true, animationDirection: 'forward', queryParams: { backoffice: this.handleErrorS.JWT_L$ } });
      await StorageData.removeDataStorage(NativeStorageNamesV2.ProgramaTenderos);
    }
    this.eventLoadRoute$.next(false);
  }

  public clearDataProgram = async (dataStorage: ApplicationUser) => {
    await firstValueFrom(this.eventLoadRoute$.pipe(debounceTime(500), first()));
    this.eventLoadRoute$.complete();
    if (this.stopInitAfterView) {
      return;
    }
    this.sharedS.isUrlWithTokenWA$.next(false);
    // 0: ID_Bodega - 1: NUD - 2: ID_Programa - 3: ID_TenderoRegistro - 4: ID_Tendero - 5: Menu
    const obtieneNativeStorage = await StorageData.getDataStorage(NativeStorageNamesV2.BodegaNud);
    if (obtieneNativeStorage) {
      this.sharedS.navigateUserData(MenuTabs);
      const userData = {
        ID_Tendero: obtieneNativeStorage[4]
        , TokenFirebase: ''
        , Token: dataStorage.token
        , Version: `v${environment.versionNumber} (${environment.versionCode})`
        , DeviceID: dataStorage.uniqueDeviceID
        , DeviceInfo: dataStorage.deviceInfo
        , offset: 0
      };
      this.store.dispatch(updatedUserData(userData));

      await StorageData.removeDataStorage(NativeStorageNamesV2.ProgramaTenderos);

      this.store.select('updatedUser')
        .pipe(
          skipWhile(x => !x.success && !x.failed),
          first(),
          takeWhile(() => !this.stopInitAfterView),
        )
        .subscribe(this.updatedData);
      await SplashScreen.hide();
    } else {
      const url = this.router.url;
      this.sharedS.emitMenu(false);
      if (NO_GUARDS.some(ng => ng === url)) {
        return;
      }
      await this.router.navigateByUrl('/main/Mas', { skipLocationChange: true });
    }
  }

  private updatedData = async (resp: UpdatedState) => {
    if (resp.success) {
      if (!resp.data.Activo) {
        this.sharedAnalyticsS.trackPageView(EAnalyticsPageViews.userDisabledView, SECTIONS.EMPTY);
        await this.sharedS.openModalDisabled(resp.data.Mensajes_Incatividad ? resp.data.Mensajes_Incatividad : '', DisabledUserComponent);
        this.analyticsS.sendEventAnalytics(GTMInteractions.acceptUserDisabledEvent, EventInteraction);
      } else if (!resp.data.Registrado) {
        this.sharedAnalyticsS.trackPageView(EAnalyticsPageViews.userUnregisterView, SECTIONS.EMPTY);
        await this.sharedS.clearData('/registro/registro');
        await this.modalsS.presentModalResetUser();
        this.analyticsS.sendEventAnalytics(GTMInteractions.acceptUserUnregisterEvent, EventInteraction);
      } else {
        this.sharedS.updatedUserData(resp.data, (resp.data.ID_TenderoRegistro || 0));
        this.legalchange = this.sharedS.validateLegalChange(resp.data.Legales || []);
        if (this.legalchange) {
          this.sharedAnalyticsS.trackPageView(EAnalyticsPageViews.legalsUpdatedView, SECTIONS.LEGALES);
          await this.modalsS.openModalLegalChange(LegalChangePage, resp.data.Legales || []);
        }
      }
    }

    if (resp.error) {
      await this.crashlytics.sendCrashPlatform(GTMEventsCrash.updatedDataApi, resp.error);
    }
  }

  /**
   * Espera a cierto tiempo para el envio del carrito a la BD
   */
  private callbackCartSend = async (resp: CartProductsState) => {
    if (resp.updateCart) {
      this.store.dispatch(sendCart());
    }
  }

  /**
   * Actualiza el contador de bloqueos
   */
  private callbackCounterSave = (resp: CountersSaveState) => {
    if (resp.success) {
      this.store.dispatch(setCounter({ ID_Flujo: resp.ID_Flujo, ...resp.data }));
    }
  }

  /**
   * Sends an environment event to the analytics service.
   */
  public sendEnvironmentEvent = (): void => {
    const event = {
      ...EAnalyticsEvents.environment
    };
    this.analyticsS.sendEventGTM(event, { addEnvironmentProps: true });

  }

  /**
   * Envía un evento de interacción con un modal promocional.
   *
   * @param params.callToAction - Identificador o tipo de acción ejecutada.
   * @param params.callToActionText - Texto asociado a la acción del usuario.
   * @param params.idCatpopup - ID del popup o categoría, puede ser null.
   * @param params.descripcion - Descripción del popup.
  */
  private sendEventInteractionPromoModal(params: PromoModalInteractionParams) {
    const { callToAction, callToActionText, descripcion, idCatpopup, title, interactionType } = params;
    const ctaText = this.translate.instant(callToActionText);
    const event: IInteractionModalEvent = {
      ...EAnalyticsModals.promotionModalRegistroInteraction,
      interaction_type: interactionType || 'cta_click',
      modal_title: title,
      modal_id: `${descripcion}_${idCatpopup}`,
      cta_id: callToAction,
      cta_text: ctaText,
      section: ESectionProperty.tienda,
    };
    this.analyticsS.sendEventInteractionModal(event);
  }

  /**
   * Envía un evento de visualización de un modal promocional.
   *
   * @param params.idCatpopup - ID del popup o categoría, puede ser null.
   * @param params.descripcion - Descripción del popup.
   * @param params.type - Tipo de popup.
  */
  private sendEventViewPromoModal(params: PromoModalViewParams) {
    const { idCatpopup, descripcion, type, title } = params;
    const event = {
      ...EAnalyticsModals.promotionModalRegistro,
      modal_id: `${descripcion}_${idCatpopup}`,
      modal_title: title,
      modal_type: type,
      page_id: "tienda_lista_productos",
      page_category: "tienda",
      section: ESectionProperty.tienda,
    }
    this.analyticsS.sendEventViewModal(event);
  }
}
